import React, { useEffect, useState } from 'react';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';

// −/+ with a centred field and unit suffix. When editing an existing figure,
// pass `previousValue` + `wasLabel` (already translated, e.g. t('Was')) to
// show "Was 128 · +14".
const QuantityStepper = ({ value, unit, min = 0, max, onChange, previousValue, wasLabel }) => {
  // The field edits a local string so it can be freely cleared and retyped
  // (onChange used to fire on every keystroke against the controlled numeric
  // `value`, so an empty/interim value clamped straight back to `min` before
  // the next digit could land). `onChange` — which for cart rows triggers a
  // network update — now only fires once editing settles, on blur or Enter.
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);

  const clamp = (next) => {
    let v = next;
    if (min != null) v = Math.max(min, v);
    if (max != null) v = Math.min(max, v);
    return v;
  };

  const commit = (raw) => {
    const parsed = Number(raw);
    const next = raw === '' || Number.isNaN(parsed) ? value : clamp(parsed);
    setText(String(next));
    if (next !== value) onChange(next);
  };

  const step = (delta) => onChange(clamp(value + delta));

  const delta = previousValue != null ? value - previousValue : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={() => step(-1)} disabled={min != null && value <= min}>
          <RemoveIcon fontSize="small" />
        </IconButton>
        <TextField
          value={text}
          onChange={(event) => setText(event.target.value.replace(/[^0-9]/g, ''))}
          onBlur={() => commit(text)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit(text);
              event.target.blur();
            }
          }}
          type="text"
          inputProps={{ style: { textAlign: 'center' }, inputMode: 'numeric' }}
          sx={{ width: 88 }}
        />
        <IconButton onClick={() => step(1)} disabled={max != null && value >= max}>
          <AddIcon fontSize="small" />
        </IconButton>
        {unit && <Typography variant="body2" color="text.secondary">{unit}</Typography>}
      </Box>
      {previousValue != null && delta !== 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {wasLabel} {previousValue} · {delta > 0 ? '+' : ''}{delta}
        </Typography>
      )}
    </Box>
  );
};

export default QuantityStepper;
